import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Box,
  Chip, CircularProgress, Select, MenuItem, FormControl,
} from '@mui/material';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import DataTablePagination from '../../components/Table/DataTablePagination';
import SearchField from '../../components/Table/SearchField';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useListPage } from '../../hooks/useListPage';

const ACTION_LABELS = {
  USER_LOGIN: { label: 'Login', color: '#3b82f6', bg: '#eff6ff' },
  USER_CREATED: { label: 'User Created', color: '#16a34a', bg: '#f0fdf4' },
  USER_UPDATED: { label: 'User Updated', color: '#ca8a04', bg: '#fefce8' },
  USER_DELETED: { label: 'User Deleted', color: '#dc2626', bg: '#fef2f2' },
  USER_SUSPENDED: { label: 'User Suspended', color: '#ea580c', bg: '#fff7ed' },
  USER_ACTIVATED: { label: 'User Activated', color: '#16a34a', bg: '#f0fdf4' },
  MAINTENANCE_ENABLED: { label: 'Maintenance ON', color: '#7c3aed', bg: '#f5f3ff' },
  MAINTENANCE_DISABLED: { label: 'Maintenance OFF', color: '#0891b2', bg: '#ecfeff' },
  APPLICATION_STATUS_CHANGED: { label: 'App Status Changed', color: '#ca8a04', bg: '#fefce8' },
  CONTRACT_TERMINATED: { label: 'Contract Terminated', color: '#dc2626', bg: '#fef2f2' },
  CONTRACT_COMPLETED: { label: 'Contract Completed', color: '#16a34a', bg: '#f0fdf4' },
  SUPPLIER_DEPARTMENT_ASSIGNED: { label: 'Department Assigned', color: '#0891b2', bg: '#ecfeff' },
  SUPPLIER_DEPARTMENT_CHANGED: { label: 'Department Changed', color: '#ca8a04', bg: '#fefce8' },
  DEPARTMENT_RENAMED: { label: 'Department Renamed', color: '#7c3aed', bg: '#f5f3ff' },
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  // A new search or filter restarts at the first page.
  const [page, setPage] = useListPage(JSON.stringify([debouncedSearch, rowsPerPage, actionFilter]));
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, actionFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/audit-logs', {
        params: { page, limit: rowsPerPage, search: debouncedSearch || undefined, action: actionFilter || undefined }
      });
      setLogs(res.data.data || []);
      setPagination(res.data.pagination || { total: 0, pages: 1 });
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleActionFilter = (val) => {
    setActionFilter(val);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getActionChip = (action) => {
    const meta = ACTION_LABELS[action] || { label: action, color: '#6b7280', bg: '#f3f4f6' };
    return (
      <Chip
        label={meta.label}
        size="small"
        sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600, fontSize: '12px', height: '24px', borderRadius: '12px', '& .MuiChip-label': { px: 1.25 } }}
      />
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: '#111827', fontSize: '28px', letterSpacing: '-0.01em' }}>
            Audit Logs
          </Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '14px' }}>
            Track all actions performed on the platform
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search by name or email"
            sx={{ minWidth: 200, maxWidth: 320 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select value={actionFilter} onChange={(e) => handleActionFilter(e.target.value)} displayEmpty sx={{ borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}>
              <MenuItem value="">All Actions</MenuItem>
              {Object.entries(ACTION_LABELS).map(([key, { label }]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            <Table sx={{ minWidth: { xs: 600, md: 800 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Action</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Performed By</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Target</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', lg: 'table-cell' } }}>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: '#6b7280', fontSize: '13px' }}>No audit logs found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log._id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell sx={{ fontSize: '13px', color: '#374151', py: 1.5, whiteSpace: 'nowrap' }}>
                        {formatDate(log.createdAt)}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>{getActionChip(log.action)}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#111827', fontWeight: 500 }}>{log.performedByName || '-'}</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>{log.performedByEmail || ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', color: '#374151', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>
                        {log.targetName || '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', color: '#9ca3af', py: 1.5, display: { xs: 'none', lg: 'table-cell' } }}>
                        {log.ipAddress || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              page={page}
              total={pagination.total || 0}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              rowsPerPageOptions={[20, 50, 100]}
              loading={loading}
            />
          </TableContainer>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default AuditLogs;
